// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import {
    getIsApplePlatform,
    getReleaseTypeForNonBlockingSteps,
    isGooglePlatform,
    isNonBlockingStepForReleaseType,
    isReleaseStepType,
} from '@/helpers';
import { RootState } from '@/reducers/reducers';
import {
    createFeatureFlagsRoute,
    createReleaseDependenciesRoute,
    createReleaseCalendarRoute,
    createReleaseOverviewRoute,
    createReleaseRolloutRoute,
    createReleaseStepRoute,
    createReleaseStepTabRoute,
    createReleaseTriageRoute,
    createStepRunRoute,
    createStepRunTabRoute,
    ReleaseStepTabType,
    createTimelineEventsRoute,
    createStepRunsSummaryRoute,
    shouldLinkToStepRunsSummary,
} from '@/helpers/routeHelpers';
import {
    getDependenciesTabVisited,
    getTriageTabVisited,
    setDependenciesTabVisited,
    setTriageTabVisited,
} from '@/helpers/localStorageHelper';
import { AppPlatform, ReleaseStatus, ReleaseStepType, ReleaseType, SubscriptionFeatureType } from '@/types';
import { antGrey, colorWithAlpha, runwayColors, rwColors, tagStyle } from '../styles/globalStyles';
import { ReleaseComponentProps } from '@/typescriptHelpers';
import {
    selectApp,
    selectAppSettings,
    selectIsTriageExcludedForOrg,
    selectHasVCSIntegrationConnected,
    selectIsAppsDemoModeEnabled,
    selectIsDemoModeEnabledForRelease,
    selectIsReleaseFromScraper,
    selectOrg,
    selectRelease,
    selectReleaseStepsBlockingConfigForReleaseType,
    selectIsRolloutPaused,
    selectIsRolloutPausedByRunway,
    selectShouldHideNonBlockingSteps,
    selectShouldHideDependenciesPage,
    selectShouldHideFeatureFlagsSection,
    selectStepRun,
    selectStepRunGraph,
    selectWorkflowEnabledSteps,
    selectIsWorkflowsCIStepEnabled,
    selectIsWorkflowsSSRStepsEnabled,
    selectDestinationInfoByDestinationStepConfigId,
    selectStepConfigDataFromStepGraphs,
    selectUserIsRunwayAdmin,
} from '@/selectors/selectors';
import {
    CalendarOutlined,
    ClockCircleOutlined,
    DashboardOutlined,
    BuildOutlined,
    FlagOutlined,
    InboxOutlined,
    SignalFilled,
} from '@ant-design/icons';
import Tag from 'antd/lib/tag';
import { ReleaseStepsSidebarNode } from './ReleaseStepsSidebarNode';
import AdditionalItem from './StepAdditionalItem';
import { RolloutBadge } from './RolloutBadge';
import { TimelineAttentionBadge } from './TimelineAttentionBadge';
import { useTimelineAttention } from '../timelineEvents/useTimelineAttention';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import Text from 'antd/lib/typography/Text';
import { matchPath, useLocation } from 'react-router-dom';
import { WorkflowRunStepRunGraph } from '@/types/workflowRunStepRunGraph';

export const filterStepRunGraphForIncludedDestinations = (
    stepRunGraph: WorkflowRunStepRunGraph,
    skippedDestinationStepConfigIds: Set<string>,
): WorkflowRunStepRunGraph => ({
    ...stepRunGraph,
    stepGroups: stepRunGraph.stepGroups.map((stepGroup) => ({
        ...stepGroup,
        stepArchetypeGroups: stepGroup.stepArchetypeGroups
            .map((archetypeGroup) => ({
                ...archetypeGroup,
                stepRunStatusDatum: archetypeGroup.stepRunStatusDatum.filter((stepRun) => {
                    if (stepRun.destinationStepConfigIds?.length) {
                        return stepRun.destinationStepConfigIds.some(
                            (destinationStepConfigId) => !skippedDestinationStepConfigIds.has(destinationStepConfigId),
                        );
                    }

                    return !stepRun.stepConfigId || !skippedDestinationStepConfigIds.has(stepRun.stepConfigId);
                }),
            }))
            .filter((archetypeGroup) => archetypeGroup.stepRunStatusDatum.length > 0),
    })),
});

const defaultStepTabForStep: (stepType: ReleaseStepType) => ReleaseStepTabType = (stepType) => {
    switch (stepType) {
        default:
            return ReleaseStepTabType.Summary;
    }
};

export const NewTag = () => (
    <Tag color={rwColors.primary} style={tagStyle}>
        NEW
    </Tag>
);

export const ReleaseStepsSidebar: React.FC<ReleaseComponentProps> = (props) => {
    const { orgId, appId, releaseId } = props;

    const location = useLocation();

    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const app = useSelector((state: RootState) => selectApp(state, appId));
    const isWorkflowsMigratedAndEnabled = useSelector(
        (state: RootState) => selectAppSettings(state, appId)?.isWorkflowsMigratedAndEnabled ?? false,
    );
    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const hasVCSConnected = useSelector((state: RootState) => selectHasVCSIntegrationConnected(state, appId));
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));
    const destinationsByStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );
    const stepConfigDataMap = useSelector((state: RootState) => selectStepConfigDataFromStepGraphs(state, appId));
    const getDestinationForStepRunId = useSelector(
        (state: RootState) => (stepRunId: string) => selectStepRun(state, stepRunId)?.destinations?.[0],
    );

    const stepRunGraph = useSelector((state: RootState) => selectStepRunGraph(state, releaseId));
    const visibleStepRunGraph = React.useMemo(() => {
        // Both are typed non-nullable but are absent at runtime for releases without destinations
        // or before the step run graph has loaded.
        if (!stepRunGraph) {
            return stepRunGraph;
        }
        const skippedDestinationStepConfigIds = new Set(
            (release.destinationSummaries ?? [])
                .filter((destination) => destination.destinationStatus === ReleaseStatus.Skipped)
                .map((destination) => destination.stepConfigId),
        );
        return filterStepRunGraphForIncludedDestinations(stepRunGraph, skippedDestinationStepConfigIds);
    }, [release.destinationSummaries, stepRunGraph]);
    const releaseIsRollback = release.type === ReleaseType.Rollback;

    const isTagNeeded =
        release ?
            release.status === ReleaseStatus.Completed && release.isReleaseTagged === false && hasVCSConnected
        :   false;

    const isReleaseDemoModeEnabled = useSelector((state: RootState) =>
        selectIsDemoModeEnabledForRelease(state, releaseId),
    );
    const isAppsDemoModeEnabled = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const showReleaseDemoModeBanner = isReleaseDemoModeEnabled && !isAppsDemoModeEnabled;
    const isRolloutPaused = useSelector((state: RootState) => selectIsRolloutPaused(state, appId, releaseId));
    const isRolloutPausedByRunway = useSelector((state: RootState) =>
        selectIsRolloutPausedByRunway(state, appId, releaseId),
    );
    const isReleaseCreatedFromScraper = useSelector((state: RootState) => selectIsReleaseFromScraper(state, releaseId));

    const shouldHideNonBlockingSteps = useSelector((state: RootState) =>
        selectShouldHideNonBlockingSteps(state, appId),
    );
    const shouldHideFeatureFlagsSection = useSelector((state: RootState) =>
        selectShouldHideFeatureFlagsSection(state, appId),
    );
    const shouldHideDependenciesPage = useSelector((state: RootState) =>
        selectShouldHideDependenciesPage(state, appId),
    );
    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, appId),
    );

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));
    const isWorkflowsSSRStepsEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsSSRStepsEnabled(state, appId),
    );

    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));
    const hasAppleDestination = Object.values(destinationsByStepConfigId).some(
        (destination) => getIsApplePlatform(destination.platform) || destination.platform === AppPlatform.IosSDK,
    );

    const isTriageExcluded = useSelector(selectIsTriageExcludedForOrg);
    const hasAndroidDestination = Object.values(destinationsByStepConfigId).some(
        (destination) => isGooglePlatform(destination.platform) || destination.platform === AppPlatform.AndroidSDK,
    );
    const shouldShowDependencies =
        isWorkflowsMigratedAndEnabled && !shouldHideDependenciesPage && (hasAppleDestination || hasAndroidDestination);
    const dependenciesRoute = createReleaseDependenciesRoute(org.orgId, app.appId, release.releaseId);
    const [showDependenciesNewTag] = React.useState(() => !getDependenciesTabVisited());
    const isOnDependenciesPage = !!matchPath(location.pathname, dependenciesRoute);
    React.useEffect(() => {
        if (isOnDependenciesPage) {
            setDependenciesTabVisited();
        }
    }, [isOnDependenciesPage]);

    const triageRoute = createReleaseTriageRoute(org.orgId, app.appId, release.releaseId);
    const [showTriageNewTag] = React.useState(() => !getTriageTabVisited());
    const isOnTriagePage = !!matchPath(location.pathname, triageRoute);
    React.useEffect(() => {
        if (isOnTriagePage) {
            setTriageTabVisited();
        }
    }, [isOnTriagePage]);

    const timelineRoute = createTimelineEventsRoute(org.orgId, app.appId, release.releaseId);
    const isOnTimelinePage = !!matchPath(location.pathname, timelineRoute);
    const { needsAttention: timelineNeedsAttention, attentionCount: timelineAttentionCount } = useTimelineAttention({
        orgId,
        appId,
        releaseId,
        isViewingTimeline: isOnTimelinePage,
        skip: releaseIsRollback,
    });

    const [expandedNodeKey, setExpandedNodeKey] = React.useState<string | null>(null);

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {showReleaseDemoModeBanner && (
                <div
                    style={{
                        backgroundColor: colorWithAlpha(rwColors.primary, 0.85),
                        color: runwayColors.white,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    This release is in demo mode.
                </div>
            )}
            <div style={{ flexGrow: 1 }}>
                <div style={{ paddingTop: 12, paddingBottom: 12 }}>
                    <ul style={{ listStyle: 'none', margin: '0px', padding: '0px' }}>
                        {!releaseIsRollback && (
                            <AdditionalItem
                                itemText={'Overview'}
                                route={createReleaseOverviewRoute(org.orgId, app.appId, release.releaseId)}
                                icon={DashboardOutlined}
                            />
                        )}
                        {!releaseIsRollback && !isTriageExcluded && (
                            <AdditionalItem
                                itemText={'Triage'}
                                leftNode={showTriageNewTag ? <NewTag /> : undefined}
                                route={triageRoute}
                                icon={InboxOutlined}
                            />
                        )}
                        {!releaseIsRollback && (
                            <AdditionalItem
                                itemText={'Timeline'}
                                leftNode={
                                    timelineNeedsAttention ?
                                        <TimelineAttentionBadge attentionCount={timelineAttentionCount} />
                                    :   undefined
                                }
                                route={timelineRoute}
                                icon={ClockCircleOutlined}
                            />
                        )}
                        {!releaseIsRollback && (
                            <AdditionalItem
                                itemText={'Schedule'}
                                route={createReleaseCalendarRoute(org.orgId, app.appId, release.releaseId)}
                                icon={CalendarOutlined}
                                isLastNode={shouldHideFeatureFlagsSection && !shouldShowDependencies}
                            />
                        )}
                        {!isWorkflowsSSRStepsEnabled && !workflowEnabledSteps[ReleaseStepType.Rollout] && (
                            <AdditionalItem
                                leftNode={
                                    <RolloutBadge
                                        isRolloutPaused={isRolloutPaused}
                                        isPausedByRunway={isRolloutPausedByRunway}
                                    />
                                }
                                itemText={'Rollout'}
                                route={createReleaseRolloutRoute(org.orgId, app.appId, release.releaseId)}
                                icon={SignalFilled}
                                isLastNode={releaseIsRollback}
                            />
                        )}
                        {!releaseIsRollback && !shouldHideFeatureFlagsSection && (
                            <AdditionalItem
                                itemText={'Feature flags'}
                                route={createFeatureFlagsRoute(org.orgId, app.appId, release.releaseId)}
                                icon={FlagOutlined}
                                isLastNode={!shouldShowDependencies}
                            />
                        )}
                        {!releaseIsRollback && shouldShowDependencies && (
                            <AdditionalItem
                                itemText={'Dependencies'}
                                leftNode={showDependenciesNewTag ? <NewTag /> : undefined}
                                route={dependenciesRoute}
                                icon={BuildOutlined}
                                isLastNode
                            />
                        )}
                    </ul>
                </div>
            </div>
            <div style={{ flexGrow: 1, paddingTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingLeft: 16, paddingRight: 16 }}>
                    <Text
                        style={{
                            fontWeight: 400,
                            color: antGrey.grey7,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            fontSize: 12,
                        }}
                    >
                        Release steps
                    </Text>
                </div>
                {visibleStepRunGraph.stepGroups.map((group, groupKeyIndex) => {
                    // filtered first so that we can get the correct index for the group and determine if it is the last/first node
                    const filteredArchetypeGroups = group.stepArchetypeGroups.filter((group) => {
                        // Since rollout is not an actual step for non-WF/legacy apps, we hide it if SSR is not enabled
                        if (
                            group.archetype === ReleaseStepType.Rollout &&
                            !isWorkflowsSSRStepsEnabled &&
                            !workflowEnabledSteps[ReleaseStepType.Rollout]
                        ) {
                            return false;
                        }

                        if (shouldHideNonBlockingSteps) {
                            return !isNonBlockingStepForReleaseType(
                                nonBlockingStepsConfigMap[getReleaseTypeForNonBlockingSteps(release.type)],
                                group.archetype,
                            );
                        }

                        return true;
                    });

                    const massagedFilteredArchetypeGroups = filteredArchetypeGroups.map(
                        (archetypeGroup, stepArchetypeIndex) => {
                            const isLastNode = stepArchetypeIndex === filteredArchetypeGroups.length - 1;
                            const isFirstNode = stepArchetypeIndex === 0;

                            const isNonBlocking = isNonBlockingStepForReleaseType(
                                nonBlockingStepsConfigMap[getReleaseTypeForNonBlockingSteps(release.type)],
                                archetypeGroup.archetype,
                            );

                            const isTagNeededForStep = isTagNeeded && isReleaseStepType(archetypeGroup.archetype);

                            const orgId = org.orgId;
                            const appId = app.appId;
                            const releaseIdParam = release.releaseId;
                            const stepArchetype = archetypeGroup.archetype;

                            const allStepRunPaths = archetypeGroup.stepRunStatusDatum.map((stepRunStatusData) => {
                                if (!stepRunStatusData.stepRunId) {
                                    return createReleaseStepRoute(orgId, appId, releaseIdParam, stepArchetype);
                                }

                                return createStepRunRoute(orgId, appId, releaseIdParam, stepRunStatusData.stepRunId);
                            });

                            const allStepRunTabPaths = archetypeGroup.stepRunStatusDatum.map((stepRunStatusData) => {
                                if (!stepRunStatusData.stepRunId) {
                                    return createReleaseStepTabRoute(
                                        orgId,
                                        appId,
                                        releaseIdParam,
                                        stepArchetype,
                                        defaultStepTabForStep(stepArchetype),
                                    );
                                }

                                return createStepRunTabRoute(
                                    orgId,
                                    appId,
                                    releaseIdParam,
                                    stepRunStatusData.stepRunId,
                                    defaultStepTabForStep(stepArchetype),
                                );
                            });

                            const doesMatchAtLeastOnePath = !!matchPath(location.pathname, {
                                path: [...allStepRunPaths, ...allStepRunTabPaths],
                            });

                            const doesMatchTheStepRunSummaryPath = !!matchPath(location.pathname, {
                                path: createStepRunsSummaryRoute(
                                    orgId,
                                    appId,
                                    releaseIdParam,
                                    groupKeyIndex,
                                    stepArchetypeIndex,
                                ),
                            });

                            return {
                                ...archetypeGroup,
                                isLastNode,
                                isFirstNode,
                                isNonBlocking,
                                isTagNeededForStep,
                                doesMatchAtLeastOnePath,
                                doesMatchTheStepRunSummaryPath,
                                allStepRunTabPaths,
                                allStepRunPaths,
                            };
                        },
                    );

                    return (
                        <div style={{ paddingTop: 12, paddingBottom: 12 }} key={groupKeyIndex}>
                            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                                {massagedFilteredArchetypeGroups.map(
                                    (
                                        {
                                            archetype,
                                            isLastNode,
                                            isFirstNode,
                                            isNonBlocking,
                                            isTagNeededForStep,
                                            stepRunStatusDatum,
                                            doesMatchAtLeastOnePath,
                                            doesMatchTheStepRunSummaryPath,
                                            allStepRunPaths,
                                            allStepRunTabPaths,
                                            combinedStatus,
                                            name,
                                        },
                                        stepArchetypeIndex,
                                    ) => {
                                        const possiblePreviousNode =
                                            massagedFilteredArchetypeGroups[stepArchetypeIndex - 1];
                                        const previousNodeHasSubStepsAndIsExpanded =
                                            possiblePreviousNode?.doesMatchAtLeastOnePath &&
                                            possiblePreviousNode?.stepRunStatusDatum.length > 1;

                                        const shouldShowStepRunSummary = shouldLinkToStepRunsSummary(
                                            archetype,
                                            stepRunStatusDatum.length,
                                            isRunwayAdmin,
                                        );

                                        const stepDestinations = stepRunStatusDatum
                                            .map((statusData) => {
                                                // 1. Direct lookup by step config ID
                                                if (
                                                    statusData.stepConfigId &&
                                                    destinationsByStepConfigId[statusData.stepConfigId]
                                                ) {
                                                    return destinationsByStepConfigId[statusData.stepConfigId];
                                                }
                                                // 2. Resolve via step graph: stepConfigId → destinationStepConfigIds → destination
                                                if (statusData.stepConfigId) {
                                                    const stepGraphEntry = stepConfigDataMap[statusData.stepConfigId];
                                                    const destStepConfigId =
                                                        stepGraphEntry?.destinationStepConfigIds?.[0];
                                                    if (
                                                        destStepConfigId &&
                                                        destinationsByStepConfigId[destStepConfigId]
                                                    ) {
                                                        return destinationsByStepConfigId[destStepConfigId];
                                                    }
                                                }
                                                // 3. Fallback to StepRun entity (available after visiting the step page)
                                                if (statusData.stepRunId) {
                                                    return getDestinationForStepRunId(statusData.stepRunId);
                                                }
                                                return undefined;
                                            })
                                            .filter(Boolean);

                                        const appPlatforms = stepDestinations.map((dest) => dest.platform);
                                        const appStoreTypes = stepDestinations.map((dest) => dest.appStoreType);

                                        const nodeKey = `${groupKeyIndex}-${stepArchetypeIndex}`;
                                        return (
                                            <li key={stepArchetypeIndex + archetype}>
                                                <PlanRestrictedFeatureWrapper
                                                    feature={SubscriptionFeatureType.ApprovalsStep}
                                                >
                                                    {(isApprovalsStepRestricted) => (
                                                        <ReleaseStepsSidebarNode
                                                            workflowEnabledSteps={workflowEnabledSteps}
                                                            previousNodeHasSubStepsAndIsExpanded={
                                                                previousNodeHasSubStepsAndIsExpanded
                                                            }
                                                            stepRunStatusDatum={stepRunStatusDatum}
                                                            isTagNeeded={isTagNeededForStep}
                                                            isFirstNode={isFirstNode}
                                                            isLastNode={isLastNode}
                                                            isNonBlocking={isNonBlocking}
                                                            allStepRunPaths={allStepRunPaths}
                                                            allStepRunTabPaths={allStepRunTabPaths}
                                                            stepArchetype={archetype}
                                                            isActive={
                                                                doesMatchAtLeastOnePath ||
                                                                (shouldShowStepRunSummary &&
                                                                    doesMatchTheStepRunSummaryPath)
                                                            }
                                                            isExpanded={expandedNodeKey === nodeKey}
                                                            onExpandChange={(expanded) =>
                                                                setExpandedNodeKey(expanded ? nodeKey : null)
                                                            }
                                                            appPlatform={appPlatforms}
                                                            appStoreType={appStoreTypes}
                                                            isPlanRestricted={
                                                                isApprovalsStepRestricted &&
                                                                archetype === ReleaseStepType.Approvals
                                                            }
                                                            isFromScraper={isReleaseCreatedFromScraper}
                                                            isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                                                            combinedStatus={combinedStatus}
                                                            customArchetypeGroupName={name}
                                                            destinations={stepDestinations}
                                                            stepRunsSummaryRoute={
                                                                shouldShowStepRunSummary ?
                                                                    createStepRunsSummaryRoute(
                                                                        org.orgId,
                                                                        app.appId,
                                                                        release.releaseId,
                                                                        groupKeyIndex,
                                                                        stepArchetypeIndex,
                                                                    )
                                                                :   undefined
                                                            }
                                                        />
                                                    )}
                                                </PlanRestrictedFeatureWrapper>
                                            </li>
                                        );
                                    },
                                )}
                            </ul>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
